/**
 * Collection Filter Buttons
 * Client-side product filtering by tags from collection metafield buttons.
 *
 * Integration with Shopify facets.js:
 * - Uses a MutationObserver to re-apply filters after AJAX reloads (e.g. sorting).
 * - Injects a hidden input into the facets form to persist filter state in URLs.
 * - Handles redirects from Shopify's native /collections/{handle}/{tag} path URLs.
 */
(function CollectionFilterButtons() {
  'use strict';

  var SELECTORS = {
    button: '[data-filter-button]',
    productItem: '.product-grid__item',
    gridContainer: '.product-grid-container',
    facetsForm: 'facets-form-component form'
  };

  var state = {
    activeFilter: 'all'
  };

  function init() {
    var buttons = document.querySelectorAll(SELECTORS.button);
    if (!buttons.length) return;

    // 1. Handle initial redirection if path-based tag URL is used
    // This converts /collections/handle/tag to /collections/handle?filter=tag
    // so that ALL products are loaded for client-side filtering.
    if (redirectFromPathFilter()) return;

    // 2. Initial state from URL
    var params = new URLSearchParams(window.location.search);
    state.activeFilter = (params.get('filter') || 'all').toLowerCase();

    // 3. Bind click handlers
    buttons.forEach(function(button) {
      button.addEventListener('click', function() {
        if (!(button instanceof HTMLElement)) return;
        var filterTag = (button.getAttribute('data-filter-button') || 'all').toLowerCase();
        
        applyFilterState(filterTag, buttons);
      });
    });

    // 4. Initial application
    syncUIWithState(buttons);
    filterProducts(state.activeFilter);
    ensureHiddenInput();

    // 5. Observe DOM changes (for AJAX reloads like sorting)
    setupObservers(buttons);
  }

  /**
   * Main entry point for updating state and UI
   */
  function applyFilterState(filterTag, buttons) {
    state.activeFilter = filterTag;
    
    syncUIWithState(buttons);
    filterProducts(state.activeFilter);
    updateURLAndForm(state.activeFilter);
  }

  /**
   * Update active class on buttons
   */
  function syncUIWithState(buttons) {
    buttons.forEach(function(btn) {
      if (!(btn instanceof HTMLElement)) return;
      var tag = (btn.getAttribute('data-filter-button') || 'all').toLowerCase();
      if (tag === state.activeFilter) {
        btn.classList.add('is-active');
      } else {
        btn.classList.remove('is-active');
      }
    });
  }

  /**
   * Detect /collections/{handle}/{tag} path URLs and redirect to ?filter= format.
   */
  function redirectFromPathFilter() {
    var path = window.location.pathname;
    var match = path.match(/^(\/collections\/[^/]+)\/(.+)/);
    if (!match) return false;

    var basePath = match[1];
    var filterSegment = decodeURIComponent(match[2] || '').replace(/\/$/, '');
    if (!filterSegment) return false;

    // Redirect to the base collection URL with ?filter= param
    var newURL = basePath + '?filter=' + encodeURIComponent(filterSegment) + window.location.search;
    window.location.replace(newURL);
    return true;
  }

  /**
   * Show/hide product cards
   */
  function filterProducts(filterTag) {
    var productItems = document.querySelectorAll(SELECTORS.productItem);
    productItems.forEach(function(item) {
      if (!(item instanceof HTMLElement)) return;

      if (filterTag === 'all') {
        item.style.display = '';
        return;
      }

      var tags = (item.getAttribute('data-tags') || '').toLowerCase();
      var filterLower = filterTag.toLowerCase().replace(/-/g, ' ');

      // Split and check for match (flexible matching for hyphens/spaces/exact)
      var tagList = tags.split(',').map(function(t) { return t.trim(); });
      var hasMatch = tagList.some(function(t) {
        return t === filterLower || t === filterTag.toLowerCase() || t.replace(/-/g, ' ') === filterLower;
      });

      if (hasMatch) {
        item.style.display = '';
      } else {
        item.style.display = 'none';
      }
    });
  }

  /**
   * Persist state to URL and facets form
   */
  function updateURLAndForm(filterTag) {
    // Update URL param without reload
    var url = new URL(window.location.href);
    if (filterTag === 'all') {
      url.searchParams.delete('filter');
    } else {
      url.searchParams.set('filter', filterTag);
    }
    history.replaceState(null, '', url.pathname + url.search);

    ensureHiddenInput();
  }

  /**
   * Ensure the facets form has our hidden input so it persists during sort reloads
   */
  function ensureHiddenInput() {
    var form = document.querySelector(SELECTORS.facetsForm);
    if (!form) return;

    var input = form.querySelector('input[name="filter"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'filter';
      form.appendChild(input);
    }
    input.value = state.activeFilter === 'all' ? '' : state.activeFilter;
  }

  /**
   * Watch for grid updates and form re-renders
   */
  function setupObservers(buttons) {
    // 1. Observe the product grid container for AJAX updates
    var gridContainer = document.querySelector(SELECTORS.gridContainer);
    if (gridContainer) {
      var gridObserver = new MutationObserver(function() {
        // Re-apply filter to new items
        filterProducts(state.activeFilter);
      });
      gridObserver.observe(gridContainer, { childList: true, subtree: true });
    }

    // 2. Observe the facets block for re-renders (preserves the hidden input)
    var facetsBlock = document.querySelector('facets-form-component');
    if (facetsBlock) {
      var facetsObserver = new MutationObserver(function() {
        ensureHiddenInput();
        // Also re-apply active state to buttons if they were re-rendered
        var newButtons = document.querySelectorAll(SELECTORS.button);
        if (newButtons.length) {
          syncUIWithState(newButtons);
        }
      });
      facetsObserver.observe(facetsBlock, { childList: true, subtree: true });
    }
  }

  // Run on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
